/**
 * <TutorOfferPage> — une offre de cours ouverte par un lien (page).
 *
 * Legacy `GroupAttributionProposalScreen` (route `group-attribution-proposals`) et
 * `SubstitutionProposalScreen` (route `substitution-proposals`) : la même page pour les deux, ouverte
 * par la notification, la carte de l'accueil ou le lien d'un mail (`?hash=…`, sans session).
 *
 * - **En attente** : le titre de l'offre, ce qu'elle dit (`<TutorOfferDetails>`), et au pied deux
 *   actions — « Accepter la proposition », qui ouvre la confirmation (`<TutorOfferFlow entry="page">`),
 *   et « Masquer la proposition », qui part sans confirmation, comme dans le legacy.
 * - **Un changement de créneau en attente** : sa question (« Garder Léa en maths ? »), ses lignes ;
 *   « Accepter la proposition » ouvre le choix de ses horaires, « Je ne peux pas » sa confirmation
 *   (`<TutorOfferFlow>` à `decline`). L'ancienne page renvoyait à l'onglet « Propositions ».
 * - **Plus en attente** : « Dommage ! » et pourquoi — l'offre n'est plus disponible, ou un autre
 *   masteur a déjà pris le remplacement.
 * - **Introuvable** : « Cette demande n'est plus d'actualité ».
 * - **C'est parti** : la boîte « Succès » du legacy, avec sa phrase (`successMessage`).
 *
 * « Retour » était un bouton texte sous les actions : c'est la flèche de l'en-tête. La Zoé du legacy
 * (contente, puis triste) n'est pas reprise : un champion n'apparaît pas sur un écran d'erreur.
 *
 * Contrôlée : l'offre, l'envoi, la confirmation et la boîte de succès arrivent en props.
 */
import React from "react"

import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { Title } from "../../atoms/typography/Title"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { EmptyState } from "../../molecules/EmptyState"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { Header } from "../../molecules/Header"
import { TutorOfferDetails } from "../../organisms/TutorOfferDetails"
import { PageTemplate } from "../../templates/PageTemplate"
import { TutorOfferFlow, tutorOfferTitle, type TutorOfferFlowProps } from "../../templates/TutorOfferFlow"
import { TUTOR_LIST_WIDTH } from "../TutorOffersPage/TutorOffersPage"
import { TutorOfferSkeleton } from "./TutorOfferSkeleton"
import { type UITutorOffer } from "../../../ports/primary/ui/types/tutorDispoOffres"

const TEXTS = {
    header: "Offre de cours",
    back: "Retour",
    accept: "Accepter la proposition",
    hide: "Masquer la proposition",
    decline: "Je ne peux pas",
    gone: "Dommage !",
    goneGroup: "Cette proposition n'est plus disponible.",
    goneSubstitution: "Un tuteur a déjà accepté ce remplacement. Une autre fois peut-être !",
    notFound: "Cette demande n'est plus d'actualité",
    success: "Succès",
    ok: "OK",
}

export type TutorOfferPageProps = {
    /** `null` : introuvable (une fois le chargement fini). */
    offer: UITutorOffer | null
    onBack: () => void
    /** Ouvre la confirmation (le choix des horaires d'un changement de créneau). */
    onAccept: () => void
    /** Masque l'offre ; ouvre « Je ne peux pas ? » sur un changement de créneau. */
    onHide: () => void
    /** La réponse qui part : les deux actions ne répondent plus. */
    sending?: "accept" | "hide" | null
    /** La confirmation, quand elle est ouverte. */
    flow?: TutorOfferFlowProps | null
    /** « La proposition a été acceptée… », « La proposition a été masquée. » */
    successMessage?: string | null
    onDismissSuccess?: () => void
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorOfferPage(props: TutorOfferPageProps) {
    const {
        offer,
        onBack,
        onAccept,
        onHide,
        sending = null,
        flow,
        successMessage,
        onDismissSuccess,
        loading = false,
        error,
        onRetry,
        testID,
    } = props
    const pending = offer?.status === "pending"
    const busy = sending != null

    const header = (
        <Header
            title={TEXTS.header}
            left={
                <IconButton
                    iconName="arrow-left"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={onBack}
                    accessibilityLabel={TEXTS.back}
                />
            }
        />
    )

    const footer =
        offer != null && pending ? (
            <Stack gap="2">
                <Button
                    label={TEXTS.accept}
                    onPress={onAccept}
                    disabled={busy}
                    loading={sending === "accept"}
                    fullWidth
                />
                <Button
                    label={offer.slotChange != null ? TEXTS.decline : TEXTS.hide}
                    variant="outline"
                    color="danger"
                    onPress={onHide}
                    disabled={busy}
                    loading={sending === "hide"}
                    fullWidth
                />
            </Stack>
        ) : undefined

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorOfferSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={TUTOR_LIST_WIDTH}
            footer={footer}
        >
            {offer == null ? (
                <EmptyState title={TEXTS.notFound} />
            ) : pending ? (
                <Stack gap="4">
                    <Title size="text-2xl">{tutorOfferTitle(offer)}</Title>
                    <TutorOfferDetails detail={offer.detail} />
                </Stack>
            ) : (
                <FlowOutcome
                    center
                    iconName="calendar-times"
                    message={TEXTS.gone}
                    detail={offer.kind === "substitution" ? TEXTS.goneSubstitution : TEXTS.goneGroup}
                >
                    <Button label={TEXTS.back} variant="outline" onPress={onBack} />
                </FlowOutcome>
            )}
            {flow != null && <TutorOfferFlow {...flow} />}
            {successMessage != null && (
                <ConfirmDialog
                    visible
                    title={TEXTS.success}
                    message={successMessage}
                    confirmLabel={TEXTS.ok}
                    danger={false}
                    hideCancel
                    onConfirm={onDismissSuccess ?? onBack}
                    onCancel={onDismissSuccess ?? onBack}
                />
            )}
        </PageTemplate>
    )
}
